import { effect, type ReadonlySignal, signal } from '@preact/signals-core';

import type { Row } from '../../../../core/view-manager/row.js';
import type { GanttZoomLevel } from '../../define.js';
import type { BarState } from '../../extensions/types.js';
import type { GanttSingleView } from '../../gantt-view-manager.js';
import { TimeAxisMath } from '../time-axis-math.js';
import { NodeLifeCycle } from './node-lifecycle.js';
import { PositionCache } from './position-cache.js';
import { GanttViewport, type ViewportMeasurements } from './viewport.js';

const DEFAULT_ROW_BUFFER = 10;

export interface VirtualScrollerOptions {
  readonly view: GanttSingleView;
  readonly rowHeight: number;
  readonly getScrollContainer: () => HTMLElement | null;
  readonly rowBuffer?: number;
  /**
   * Called after every frame emit — caller typically triggers a render.
   * The frame itself is always readable from {@link VirtualScroller.frame$}
   * so Lit components can subscribe via SignalWatcher.
   */
  readonly onFrame?: (frame: VirtualScrollFrame) => void;
  /**
   * Overrides {@link window.requestAnimationFrame} — unit tests
   * pass a synchronous callback to make assertions deterministic.
   */
  readonly scheduler?: (callback: () => void) => void;
}

export interface VirtualScrollFrame {
  readonly viewport: GanttViewport;
  readonly visibleRows: Row[];
  readonly visibleBars: BarState[];
  readonly zoom: GanttZoomLevel;
  readonly originMs: number;
}

/**
 * Drives the gantt virtualization. Subscribes to scroll + signal
 * changes that affect the visible window, throttles through the
 * configured scheduler, and emits a `VirtualScrollFrame` with the
 * trimmed row/bar lists.
 *
 * The position pipeline and bar-decorator hooks are invoked by the
 * `onFrame` consumer — they always see `O(visible)` items, never the
 * full row list.
 */
export class VirtualScroller extends NodeLifeCycle {
  readonly positionCache = new PositionCache();

  private readonly frameSignal = signal<VirtualScrollFrame | null>(null);
  private scheduled = false;
  private readonly scheduler: (cb: () => void) => void;

  get frame$(): ReadonlySignal<VirtualScrollFrame | null> {
    return this.frameSignal;
  }

  constructor(private readonly opts: VirtualScrollerOptions) {
    super();
    this.scheduler =
      opts.scheduler ??
      (typeof requestAnimationFrame === 'function'
        ? cb => requestAnimationFrame(cb)
        : cb => setTimeout(cb, 16));
  }

  override init(): void {
    const container = this.opts.getScrollContainer();

    if (container) {
      const onScroll = () => this.schedule();
      container.addEventListener('scroll', onScroll, { passive: true });
      this.disposables.push(() =>
        container.removeEventListener('scroll', onScroll)
      );
    }

    this.disposables.push(
      effect(() => {
        // Re-read the signals that affect the visible window so
        // effect registers the dependency.
        this.opts.view.zoomLevel$.value;
        this.opts.view.rowCount$.value;
        this.opts.view.startDateCol$.value;
        this.opts.view.endDateCol$.value;
        this.opts.view.extensions.positionTransforms.version$.value;
        this.positionCache.invalidateAll();
        this.schedule();
      })
    );

    // First frame — synchronous enough that consumers can render
    // immediately after init().
    this.emit();
  }

  /**
   * Manually force a recompute + emit. Callers use this when the
   * viewport-host resized outside the scroll path.
   */
  refresh(): void {
    this.schedule();
  }

  private schedule(): void {
    if (this.scheduled || this.isDisposed) return;
    this.scheduled = true;
    this.scheduler(() => {
      this.scheduled = false;
      if (!this.isDisposed) this.emit();
    });
  }

  private emit(): void {
    const container = this.opts.getScrollContainer();
    const measurements = measure(container);
    const zoom = this.opts.view.zoomLevel$.value;
    const originMs = this.opts.view.data$.value
      ? TimeAxisMath.floorToDayUtc(Date.now())
      : 0;
    const math = new TimeAxisMath(zoom, originMs);

    const viewport = new GanttViewport(measurements, {
      rowHeight: this.opts.rowHeight,
      rowBuffer: this.opts.rowBuffer ?? DEFAULT_ROW_BUFFER,
      timeAxis: math,
    });

    const rows = this.opts.view.getBoundedRowsWindow().rows;
    const visibleRows = viewport.visibleRows(rows);

    const bars: BarState[] = [];
    for (const row of visibleRows) {
      const state = readState(this.opts.view, row);
      if (state) bars.push(state);
    }
    const visibleBars = viewport.visibleBars(bars);

    const frame: VirtualScrollFrame = {
      viewport,
      visibleRows,
      visibleBars,
      zoom,
      originMs,
    };
    this.frameSignal.value = frame;
    this.opts.onFrame?.(frame);
  }
}

function measure(container: HTMLElement | null): ViewportMeasurements {
  if (!container) {
    return { scrollX: 0, scrollY: 0, width: 0, height: 0 };
  }
  const rect = container.getBoundingClientRect();
  const viewportHeight =
    typeof window === 'undefined' ? container.clientHeight : window.innerHeight;
  const visibleHeight = Math.max(
    0,
    Math.min(container.clientHeight, viewportHeight - Math.max(0, rect.top))
  );
  return {
    scrollX: container.scrollLeft,
    scrollY: container.scrollTop,
    width: container.clientWidth,
    height: visibleHeight,
  };
}

function readState(view: GanttSingleView, row: Row): BarState | null {
  const startCol = view.startDateCol$.value;
  const endCol = view.endDateCol$.value;
  if (!startCol || !endCol) return null;
  const startRaw = view.cellGetOrCreate(row.rowId, startCol).jsonValue$.value;
  const endRaw = view.cellGetOrCreate(row.rowId, endCol).jsonValue$.value;
  if (typeof startRaw !== 'number' || typeof endRaw !== 'number') return null;
  return { rowId: row.rowId, startMs: startRaw, endMs: endRaw };
}
